import * as moment from 'moment';
import * as momentTZ from 'moment-timezone';
import { isEmpty, isNumber, isString, toLower } from 'lodash';
import { TIME_FORMATS } from '@config/index';

export const setLocale = (locale: string): void => {
  if (isEmpty(locale) || !isString(locale)) return;
  momentTZ.locale(locale);
};

/**
 * set source date hours to start of the day and return it.
 *
 * @param sourceDate source date
 * @returns sourceDate which was reset to start of the day.
 */
export const getStartOfDay = (sourceDate: Date | string): Date => {
  const date = toDate(sourceDate);
  date.setHours(0, 0, 0, 0);

  return date;
};

/**
 * Function return dateTime moment.js object which contains local date
 * regardless of local timezone. Time is set to zero.
 * e.g. 20 Feb at 8PM in Charlotte EST (GMT-5) it still returns moment ('2019-02-20T00:00:00.000Z').
 * (note that Date.toISOString() will return 21Feb)
 */
export const getStartOfLocalDay = (date?: string): any => {
  const keepLocalTime = true;

  return toMoment(date).utcOffset(0, keepLocalTime).startOf('day');
};

/**
 * Function return dateTime string in ISO format which contains local date
 * regardless of local timezone. Time is set to zero.
 * e.g. 20 Feb at 8PM in Charlotte EST (GMT-5) it still returns 2019-02-20T00:00:00.000Z.
 * (note that Date.toISOString() will return 21Feb)
 */
export const getStartOfLocalDayISOString = (date?: string): string => {
  return getStartOfLocalDay(date).toISOString();
};

/**
 * get Date subtract string
 *
 * @param date target date
 * @param subsLength substring length
 * @param amount date range to today
 * @param unit subtract unit
 * @returns Date subtract string(UTC)
 */
export const getSubtractDateString = (
  date: Date | string = toDate(),
  subsLength: number = undefined,
  amount: number = 30,
  unit: moment.unitOfTime.DurationConstructor = 'days',
): string => {
  const subtractedDate = toMoment(date).subtract(amount, unit).toISOString();

  return subtractedDate.substring(0, subsLength);
};

/**
 * get day with diff
 *
 * @param daysDiff day diff
 * @param resetHours is need reset date to start of the day
 * @param date target date, default is today
 * @param isFuture is future date
 * @returns diffed date
 */
export const getDateWithDaysDiff = (
  daysDiff: number,
  isResetHours: boolean,
  date: Date = null,
  isFuture: boolean = false,
): Date => {
  let resultDate = toDate(date || undefined);

  const diffNumber = isFuture ? 1 : -1;

  resultDate.setDate(date.getDate() + daysDiff * diffNumber);

  if (isResetHours) {
    resultDate = getStartOfDay(resultDate);
  }

  return resultDate;
};

/**
 * get day time with diff
 *
 * @param daysDiff day diff
 * @param resetHours is need reset date to start of the day
 * @param date target date, default is today
 * @param isFuture is future date
 * @returns diffed date timer number
 */
export const getDateWithDaysDiffTime = (
  daysDiff: number,
  isResetHours: boolean,
  date: Date = null,
  isFuture: boolean = false,
): number => {
  return getDateWithDaysDiff(daysDiff, isResetHours, date, isFuture)?.getTime();
};

export const convertUTCDateToTimeZone = (
  utcDate: string,
  timeZone: string,
  format?: TIME_FORMATS,
): string => {
  if (!utcDate || isISODateString(utcDate)) {
    return momentTZ.utc(utcDate).tz(timeZone).format(format);
  }

  return momentTZ.utc(utcDate, TIME_FORMATS.L_LTS).tz(timeZone).format(format);
};

export const convertDateToUTCDate = (
  date: string,
  timeZone: string,
): string => {
  if (!date || isISODateString(date)) {
    return momentTZ.tz(date, timeZone).toISOString();
  }

  return momentTZ.tz(date, TIME_FORMATS.L_LTS, timeZone).toISOString();
};

/**
 * format date with target time zone
 *
 * @param date target date
 * @param timeZone target timeZone
 * @param format date format
 */
export const formatDateWithTimeZone = (
  date: string,
  timeZone: string,
  format?: TIME_FORMATS,
): string => {
  if (!date || isISODateString(date)) {
    return momentTZ.tz(date, timeZone).format(format);
  }

  return momentTZ.tz(date, TIME_FORMATS.L_LTS, timeZone).format(format);
};

export const getCurrentTimeByTimeZone = (
  timeZone: string,
  format?: TIME_FORMATS,
): string => {
  return momentTZ.tz(timeZone).format(format);
};

export const isUsing12HourFormat = (): boolean => {
  const fmt = toMoment().localeData().longDateFormat(TIME_FORMATS.LTS);

  return toLower(fmt).includes('a');
};

export const isISODateString = (dateString: any): boolean => {
  if (typeof dateString !== 'string') {
    return false;
  }
  const iso8601Regex =
    /^\d{4}-\d{2}-\d{2}(?:[ T]\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?(?:Z|[+-]\d{2}:?\d{2})?)?$/;

  return iso8601Regex.test(dateString);
};

type ComplexDateType = Date | moment.Moment | string | number;

export const toMoment = (data?: ComplexDateType): moment.Moment => {
  if (momentTZ.isDate(data)) {
    return momentTZ(data);
  }

  if (momentTZ.isMoment(data)) {
    return data;
  }

  if (!data || isEmpty(data) || isNumber(data)) {
    return momentTZ(data);
  }

  return momentTZ(data, isISODateString(data) ? undefined : TIME_FORMATS.L_LTS);
};

export const toDate = (data?: ComplexDateType): Date => {
  if (momentTZ.isDate(data)) {
    return data;
  }

  if (momentTZ.isMoment(data)) {
    return data.toDate();
  }

  if (!data || isEmpty(data) || isNumber(data)) {
    return momentTZ(data).toDate();
  }

  return momentTZ(
    data,
    isISODateString(data) ? undefined : TIME_FORMATS.L_LTS,
  ).toDate();
};

export const formatDateString = (
  dateString: string | Date,
  format: TIME_FORMATS = TIME_FORMATS.L,
): string => {
  return toMoment(dateString).format(format);
};
